import { useCallback, useEffect, useState } from "react";
import client from "../api/client";

/**
 * GET a path, tracking loading/error/data and cancelling in-flight requests
 * when the path changes or the component unmounts.
 *
 * @param {string|null} path pass null to skip the request
 */
const useApiQuery = (path, { select } = {}) => {
  const [state, setState] = useState({
    data: null,
    loading: Boolean(path),
    error: null,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const refetch = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!path) {
      setState({ data: null, loading: false, error: null });
      return undefined;
    }

    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true, error: null }));

    client
      .get(path, { signal: controller.signal })
      .then((response) =>
        setState({
          data: select ? select(response.data) : response.data,
          loading: false,
          error: null,
        })
      )
      .catch((error) => {
        if (controller.signal.aborted) return;
        setState({ data: null, loading: false, error });
      });

    return () => controller.abort();
    // `select` is intentionally omitted — callers pass inline functions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, reloadKey]);

  return { ...state, refetch };
};

export default useApiQuery;
